import { type JSX, createSignal, createEffect, onMount, onCleanup, Show } from 'solid-js';
import { useLocation, useNavigate } from '@solidjs/router';
import Navbar from '../components/Navbar';
import { DashboardNavbar } from '../components/DashboardNavbar';
import { useMobileSidebar } from '../hooks/useMobileSidebar';
import CommandPalette from '../components/CommandPalette';
import { Breadcrumbs } from '../components/Breadcrumbs';
import InstallAppBanner from '../components/InstallAppBanner';
import SelfShutoffBanner from '../components/SelfShutoffBanner';
import { ShortcutCheatsheet } from '../components/ShortcutCheatsheet';
import { useShortcut } from '../hooks/useShortcut';
import { useUserContext } from '../components/UserContextProvider';
import { FeatureGate } from '../components/FeatureGate';
import FloatingBotActivityCard from '../components/FloatingBotActivityCard';

export default function DashboardLayout(props: { children: JSX.Element }) {
  const { toggleSidebar } = useMobileSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useUserContext();
  const [cheatOpen, setCheatOpen] = createSignal(false);
  const [isMobile, setIsMobile] = createSignal(window.matchMedia('(max-width: 768px)').matches);

  onMount(() => {
    const mql = window.matchMedia('(max-width: 768px)');
    const update = () => setIsMobile(mql.matches);
    mql.addEventListener('change', update);
    onCleanup(() => mql.removeEventListener('change', update));
  });

  createEffect(() => {
    const u = user();
    if (u.loggedin && u.minecraftUsernameVerified && (u.permissions ?? 0) === 0) {
      navigate('/onboarding');
    }
  });

  useShortcut("?", () => setCheatOpen(true), { description: "Show keyboard shortcuts", scope: "global" });
  useShortcut("shift+/", () => setCheatOpen(true), { description: "Show keyboard shortcuts", scope: "global" });
  useShortcut("g d", () => navigate("/dashboard"), { description: "Go to Dashboard", scope: "navigation" });
  useShortcut("g b", () => navigate("/bots"), { description: "Go to Bot Registry", scope: "navigation" });
  useShortcut("g e", () => navigate("/botengine"), { description: "Go to Bot Engine", scope: "navigation" });
  useShortcut("g i", () => navigate("/inventory"), { description: "Go to Inventory", scope: "navigation" });
  useShortcut("g j", () => navigate("/jobs"), { description: "Go to Jobs", scope: "navigation" });
  useShortcut("g a", () => navigate("/auditlogs"), { description: "Go to Audit Logs", scope: "navigation" });
  useShortcut("g f", () => navigate("/activity"), { description: "Go to Activity Feed", scope: "navigation" });
  useShortcut("g p", () => navigate("/online-players"), { description: "Go to Online Players", scope: "navigation" });
  useShortcut("g s", () => navigate("/settings"), { description: "Go to Settings", scope: "navigation" });
  useShortcut("/", () => {
    const el = document.querySelector<HTMLInputElement>('[data-search-focus="true"], input[type="search"]');
    if (el) { el.focus(); el.select?.(); }
  }, { description: "Focus search input", scope: "global" });

  const hamburger = (
    <button class="hamburger-btn" onClick={toggleSidebar} aria-label="Toggle sidebar">
      <i class="fa-solid fa-bars"></i>
    </button>
  );

  return (
    <>
      <Navbar menuButton={hamburger}>
        <DashboardNavbar>
          <Breadcrumbs />
          <FeatureGate flag="SELF_SHUTOFF">
            <SelfShutoffBanner />
          </FeatureGate>
          <InstallAppBanner />
          <div class="route-fade" data-route={location.pathname}>
            {props.children}
          </div>
        </DashboardNavbar>
      </Navbar>
      <CommandPalette />
      <ShortcutCheatsheet open={cheatOpen()} onClose={() => setCheatOpen(false)} />
      <Show when={!isMobile()}>
        <FeatureGate flag="FLOATING_BOTS_CARD">
          <FloatingBotActivityCard />
        </FeatureGate>
      </Show>
    </>
  );
}
